import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, firstValueFrom, share } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { createSseStream } from '../common/sse/sse-stream';

export interface NotificationItem {
  _id: string;
  type: string;
  name: string;
  description: string;
  isRead: boolean;
  createdAt: string;
  expiresAt?: string;
  metadata?: Record<string, any>;
}

export interface NotificationTypeConfig {
  icon: string;
  bgClass: string;
  badgeClass: string;
  label: string;
  description?: string;
}

export interface NotificationSetting {
  type: string;
  enabled: boolean;
}

export const NOTIFICATION_TYPE_CONFIG: Record<string, NotificationTypeConfig> = {
  JIRA_UPDATE: {
    icon: 'pi pi-ticket',
    bgClass: 'notif-icon-bg--jira',
    badgeClass: 'notif-badge--jira',
    label: 'Jira Updates',
    description: 'Get notified when a linked Jira ticket is updated.',
  },
  BUILD_FAILED: {
    icon: 'pi pi-times-circle',
    bgClass: 'notif-icon-bg--build',
    badgeClass: 'notif-badge--build',
    label: 'Build Failed',
    description: 'Get notified when an asset build fails.',
  },
  BUILD_COMPLETE: {
    icon: 'pi pi-check-circle',
    bgClass: 'notif-icon-bg--build-complete',
    badgeClass: 'notif-badge--build-complete',
    label: 'Build Complete',
    description: 'Get notified when an asset build finishes successfully.',
  },
  GAMEDATA_BUILD_COMPLETE: {
    icon: 'pi pi-check-circle',
    bgClass: 'notif-icon-bg--gamedata-complete',
    badgeClass: 'notif-badge--gamedata-complete',
    label: 'Gamedata Build Complete',
    description: 'Get notified when your gamedata push finishes successfully.',
  },
  GAMEDATA_BUILD_FAILED: {
    icon: 'pi pi-exclamation-triangle',
    bgClass: 'notif-icon-bg--gamedata-failed',
    badgeClass: 'notif-badge--gamedata-failed',
    label: 'Gamedata Build Failed',
    description: 'Get notified when your gamedata push fails.',
  },
};

export const DEFAULT_NOTIFICATION_TYPE_CONFIG: NotificationTypeConfig = {
  icon: 'pi pi-bell',
  bgClass: 'notif-icon-bg--default',
  badgeClass: 'notif-badge--default',
  label: '',
};

export function getNotificationTypeConfig(type: string): NotificationTypeConfig {
  return NOTIFICATION_TYPE_CONFIG[type] ?? {
    ...DEFAULT_NOTIFICATION_TYPE_CONFIG,
    label: type.replace(/_/g, ' '),
  };
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private unreadCount$?: Observable<number>;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  getUnreadCount(): Promise<{ count: number }> {
    // Without a token the interceptor still sends `Bearer null`, so the API 401s — and the
    // interceptor turns that 401 into a logout plus a page reload, which re-mounts the shell
    // and fires this call again. The shell renders on a stale localStorage `loggedIn` flag, so
    // a missing token here is a normal state, not an error. createUnreadCountStream() below
    // already guards the same way.
    var token = this.authService.getToken();
    if (token == null || token == undefined || token.length == 0 || token == "null" || token == "undefined") {
      return Promise.resolve({ count: 0 });
    }
    return firstValueFrom(
      this.http.get<{ count: number }>('/api/notifications/unread-count'),
    );
  }

  /**
   * Live unread-count updates via SSE. Uses fetch (not EventSource) so the
   * JWT Authorization header can be sent. Shared across subscribers; the
   * connection closes when the last subscriber unsubscribes.
   */
  connectUnreadCountStream(): Observable<number> {
    if (!this.unreadCount$) {
      this.unreadCount$ = this.createUnreadCountStream().pipe(
        share({
          connector: () => new Subject<number>(),
          resetOnRefCountZero: true,
        }),
      );
    }
    return this.unreadCount$;
  }

  getNotifications(
    page = 0,
    pageSize = 10,
    types?: string[],
    isRead?: boolean,
  ): Promise<{ data: NotificationItem[]; total: number }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (types?.length) {
      types.forEach((t) => (params = params.append('type', t)));
    }
    if (isRead !== undefined) {
      params = params.set('isRead', String(isRead));
    }
    return firstValueFrom(
      this.http.get<{ data: NotificationItem[]; total: number }>(
        '/api/notifications',
        { params },
      ),
    );
  }

  markAsRead(id: string): Promise<NotificationItem> {
    return firstValueFrom(
      this.http.patch<NotificationItem>(`/api/notifications/${id}/read`, {}),
    );
  }

  markAllAsRead(): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>('/api/notifications/read-all', {}),
    );
  }

  deleteNotification(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`/api/notifications/${id}`));
  }

  getSettings(): Promise<NotificationSetting[]> {
    return firstValueFrom(
      this.http.get<NotificationSetting[]>('/api/notifications/settings'),
    );
  }

  updateSettings(settings: NotificationSetting[]): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>('/api/notifications/settings', { settings }),
    );
  }

  private createUnreadCountStream(): Observable<number> {
    return createSseStream<number>(this.authService, {
      url: () => '/api/notifications/stream',
      parseFrame: parseUnreadCountFrame,
    });
  }
}

function parseUnreadCountFrame(frame: string): number | null {
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('\n');
  if (!data) return null;
  try {
    const payload = JSON.parse(data);
    return typeof payload?.unreadCount === 'number' ? payload.unreadCount : null;
  } catch {
    return null;
  }
}
